import db from '../db/db'
import Database from '../types/Database'

import ErrorHandler from './ErrorHandler'

/**
 * OnboardingService
 *
 * Service class for onboarding-related business operations.
 * Handles onboarding state tracking, progress persistence, and page visit tracking.
 *
 * Requirements: 7.1, 7.2, 7.3, 7.4, 7.5, 13.1, 13.2, 13.3, 13.4, 13.5
 */
export class OnboardingService {
  /**
   * Get user's onboarding record
   *
   * Requirements:
   * - 7.1: Retrieve onboarding state from database
   * - 7.2: Return null if no record exists
   */
  static async getUserOnboarding(
    userId: string
  ): Promise<Database.UserOnboarding | null> {
    try {
      if (!userId) {
        ErrorHandler.logWarning(
          'getUserOnboarding called with empty userId',
          { userId },
          { component: 'OnboardingService', action: 'getUserOnboarding' }
        )
        return null
      }

      ErrorHandler.logInfo(
        'Fetching user onboarding record',
        { userId },
        { component: 'OnboardingService', action: 'getUserOnboarding' }
      )

      const onboarding = await db.getById('userOnboarding', userId)

      if (onboarding) {
        ErrorHandler.logInfo(
          'Successfully fetched user onboarding record',
          { userId, currentStep: onboarding.currentStep },
          { component: 'OnboardingService', action: 'getUserOnboarding' }
        )
      }

      return onboarding || null
    } catch (error) {
      const errorInstance =
        error instanceof Error ? error : new Error(String(error))

      ErrorHandler.logSystemError(
        'Error getting user onboarding',
        errorInstance,
        {
          component: 'OnboardingService',
          action: 'getUserOnboarding'
        }
      )
      throw new Error(
        `Failed to load onboarding data: ${errorInstance.message}`
      )
    }
  }

  /**
   * Create a new onboarding record for a user
   *
   * Requirements:
   * - 7.1: Create onboarding record in database
   * - 7.3: Record timestamp when onboarding was started
   */
  static async createOnboardingRecord(
    userId: string
  ): Promise<Database.UserOnboarding> {
    try {
      if (!userId) {
        const authError = new Error(
          'User ID required to create onboarding record'
        )
        ErrorHandler.logPermissionError(
          'User ID validation failed',
          { userId },
          { component: 'OnboardingService', action: 'createOnboardingRecord' }
        )
        throw authError
      }

      const now = new Date()

      const newOnboarding: Database.UserOnboarding = {
        id: userId,
        userId: userId,
        isComplete: false,
        currentStep: 'office_choice',
        startedAt: now,
        completedAt: null,
        skippedDeskCreation: false,
        hasSeenPointers: {
          officeForm: false,
          deskForm: false
        },
        hasVisitedPages: {
          calendar: false,
          booking: false
        }
      }

      ErrorHandler.logInfo(
        'Creating new onboarding record',
        { userId },
        { component: 'OnboardingService', action: 'createOnboardingRecord' }
      )

      await db.addItem('userOnboarding', newOnboarding)

      ErrorHandler.logInfo(
        'Onboarding record created successfully',
        { userId },
        { component: 'OnboardingService', action: 'createOnboardingRecord' }
      )

      return newOnboarding
    } catch (error) {
      const errorInstance =
        error instanceof Error ? error : new Error(String(error))

      ErrorHandler.logSystemError(
        'Error creating onboarding record',
        errorInstance,
        {
          component: 'OnboardingService',
          action: 'createOnboardingRecord'
        }
      )
      throw new Error(
        `Failed to create onboarding record: ${errorInstance.message}`
      )
    }
  }

  /**
   * Update the current onboarding step for a user
   *
   * Requirements:
   * - 7.1: Store the current wizard step in the database
   * - 7.2: Allow users to resume from the last completed step
   */
  static async updateOnboardingStep(
    userId: string,
    step: Database.OnboardingStep
  ): Promise<void> {
    try {
      if (!userId) {
        throw new Error('User ID required to update onboarding step')
      }

      ErrorHandler.logInfo(
        'Updating onboarding step',
        { userId, step },
        { component: 'OnboardingService', action: 'updateOnboardingStep' }
      )

      await db.updateItem('userOnboarding', userId, {
        currentStep: step
      })

      ErrorHandler.logInfo(
        'Onboarding step updated successfully',
        { userId, step },
        { component: 'OnboardingService', action: 'updateOnboardingStep' }
      )
    } catch (error) {
      const errorInstance =
        error instanceof Error ? error : new Error(String(error))

      ErrorHandler.logSystemError(
        'Error updating onboarding step',
        errorInstance,
        {
          component: 'OnboardingService',
          action: 'updateOnboardingStep'
        }
      )
      throw new Error(
        `Failed to update onboarding step: ${errorInstance.message}`
      )
    }
  }

  /**
   * Mark onboarding as complete for a user
   *
   * Requirements:
   * - 7.4: Record the completion timestamp
   * - 9.3: Mark onboarding as complete in database
   */
  static async markOnboardingComplete(userId: string): Promise<void> {
    try {
      if (!userId) {
        throw new Error('User ID required to mark onboarding complete')
      }

      const now = new Date()

      // Cache in localStorage immediately - this is the primary "show once" gate
      // Even if DB write fails, the wizard should never show again
      localStorage.setItem(`onboardingComplete_${userId}`, 'true')

      ErrorHandler.logInfo(
        'Marking onboarding as complete',
        { userId },
        { component: 'OnboardingService', action: 'markOnboardingComplete' }
      )

      await db.updateItem('userOnboarding', userId, {
        isComplete: true,
        currentStep: 'complete',
        completedAt: now
      })

      ErrorHandler.logInfo(
        'Onboarding marked as complete successfully',
        { userId },
        { component: 'OnboardingService', action: 'markOnboardingComplete' }
      )
    } catch (error) {
      const errorInstance =
        error instanceof Error ? error : new Error(String(error))

      ErrorHandler.logSystemError(
        'Error marking onboarding complete',
        errorInstance,
        {
          component: 'OnboardingService',
          action: 'markOnboardingComplete'
        }
      )
      throw new Error(
        `Failed to mark onboarding complete: ${errorInstance.message}`
      )
    }
  }

  /**
   * Mark a pointer as seen by the user
   *
   * Requirements:
   * - 8.3: Automatically hide pointers after user completes action
   * - 8.4: Don't display pointers for steps user has already completed
   */
  static async markPointerSeen(
    userId: string,
    pointer: 'officeForm' | 'deskForm'
  ): Promise<void> {
    try {
      if (!userId) {
        throw new Error('User ID required to mark pointer as seen')
      }

      // Cache in localStorage immediately - pointers should only ever show once
      localStorage.setItem(`pointerSeen_${userId}_${pointer}`, 'true')

      ErrorHandler.logInfo(
        'Marking pointer as seen',
        { userId, pointer },
        { component: 'OnboardingService', action: 'markPointerSeen' }
      )

      const updateData: Partial<Database.UserOnboarding> = {
        hasSeenPointers: {
          officeForm: pointer === 'officeForm',
          deskForm: pointer === 'deskForm'
        }
      }

      // Get current state to preserve other pointer values
      const currentOnboarding = await this.getUserOnboarding(userId)
      if (currentOnboarding) {
        updateData.hasSeenPointers = {
          ...currentOnboarding.hasSeenPointers,
          [pointer]: true
        }
      }

      await db.updateItem('userOnboarding', userId, updateData)

      ErrorHandler.logInfo(
        'Pointer marked as seen successfully',
        { userId, pointer },
        { component: 'OnboardingService', action: 'markPointerSeen' }
      )
    } catch (error) {
      const errorInstance =
        error instanceof Error ? error : new Error(String(error))

      ErrorHandler.logSystemError(
        'Error marking pointer as seen',
        errorInstance,
        {
          component: 'OnboardingService',
          action: 'markPointerSeen'
        }
      )
      throw new Error(
        `Failed to mark pointer as seen: ${errorInstance.message}`
      )
    }
  }

  /**
   * Mark a page as visited by the user
   *
   * Requirements:
   * - 13.4: Update User_State to record page visit
   * - 13.5: Persist page visit tracking across user sessions
   */
  static async markPageVisited(
    userId: string,
    page: 'calendar' | 'booking'
  ): Promise<void> {
    try {
      if (!userId) {
        throw new Error('User ID required to mark page as visited')
      }

      ErrorHandler.logInfo(
        'Marking page as visited',
        { userId, page },
        { component: 'OnboardingService', action: 'markPageVisited' }
      )

      // Get current state to preserve other page visit values
      const currentOnboarding = await this.getUserOnboarding(userId)

      const updateData: Partial<Database.UserOnboarding> = {
        hasVisitedPages: {
          calendar:
            page === 'calendar'
              ? true
              : currentOnboarding?.hasVisitedPages.calendar || false,
          booking:
            page === 'booking'
              ? true
              : currentOnboarding?.hasVisitedPages.booking || false
        }
      }

      await db.updateItem('userOnboarding', userId, updateData)

      ErrorHandler.logInfo(
        'Page marked as visited successfully',
        { userId, page },
        { component: 'OnboardingService', action: 'markPageVisited' }
      )
    } catch (error) {
      const errorInstance =
        error instanceof Error ? error : new Error(String(error))

      ErrorHandler.logSystemError(
        'Error marking page as visited',
        errorInstance,
        {
          component: 'OnboardingService',
          action: 'markPageVisited'
        }
      )
      throw new Error(
        `Failed to mark page as visited: ${errorInstance.message}`
      )
    }
  }

  /**
   * Reset onboarding for a user (for testing or user request)
   *
   * Requirements:
   * - 7.5: Allow users to restart onboarding from their profile settings
   */
  static async resetOnboarding(userId: string): Promise<void> {
    try {
      if (!userId) {
        throw new Error('User ID required to reset onboarding')
      }

      ErrorHandler.logInfo(
        'Resetting onboarding',
        { userId },
        { component: 'OnboardingService', action: 'resetOnboarding' }
      )

      const now = new Date()

      await db.updateItem('userOnboarding', userId, {
        isComplete: false,
        currentStep: 'office_choice',
        startedAt: now,
        completedAt: null,
        skippedDeskCreation: false,
        hasSeenPointers: {
          officeForm: false,
          deskForm: false
        },
        hasVisitedPages: {
          calendar: false,
          booking: false
        }
      })

      ErrorHandler.logInfo(
        'Onboarding reset successfully',
        { userId },
        { component: 'OnboardingService', action: 'resetOnboarding' }
      )
    } catch (error) {
      const errorInstance =
        error instanceof Error ? error : new Error(String(error))

      ErrorHandler.logSystemError('Error resetting onboarding', errorInstance, {
        component: 'OnboardingService',
        action: 'resetOnboarding'
      })
      throw new Error(`Failed to reset onboarding: ${errorInstance.message}`)
    }
  }
}

export default OnboardingService
